import { useState } from "react";
import { db } from "../../utils/firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,800;0,9..144,900;1,9..144,800;1,9..144,900&family=Plus+Jakarta+Sans:wght@300;400;500;600&display=swap');

    .rm-root { font-family: 'Plus Jakarta Sans', sans-serif; }
    .rm-zh   { font-family: 'Fraunces', Georgia, serif; }

    @keyframes rm-backdrop { from{opacity:0} to{opacity:1} }
    @keyframes rm-card     { from{opacity:0;transform:translateY(20px) scale(.97)} to{opacity:1;transform:translateY(0) scale(1)} }
    @keyframes rm-shimmer  { 0%{background-position:-200% center} 100%{background-position:200% center} }
    @keyframes rm-pulse    { 0%,100%{opacity:1} 50%{opacity:.3} }
    @keyframes rm-spin     { to{transform:rotate(360deg)} }

    .rm-backdrop { animation: rm-backdrop .2s both; }
    .rm-card     { animation: rm-card .3s cubic-bezier(.22,.68,0,1.2) both; }

    .rm-shine {
      background: linear-gradient(90deg,#6cbb23 0%,#a8e063 35%,#6cbb23 55%,#4d8f10 100%);
      background-size: 200% auto;
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text; animation: rm-shimmer 3.5s linear infinite;
    }

    /* ── Fields ── */
    .rm-field {
      width:100%; border:1.5px solid #ebebе7;
      border-radius:10px; padding:10px 13px;
      font-size:13.5px; font-family:'Plus Jakarta Sans',sans-serif;
      color:#111; background:#fafaf9; outline:none; resize:none;
      transition:border-color .15s, background .15s, box-shadow .15s;
      appearance:none;
    }
    .rm-field::placeholder { color:#c0c0ba; }
    .rm-field:focus {
      border-color:#6cbb23; background:#fff;
      box-shadow:0 0 0 3px rgba(108,187,35,.09);
    }
    .rm-field:hover:not(:focus) { border-color:#d0d0ca; }

    /* ── Buttons ── */
    .rm-btn-cancel {
      flex:1; border:1.5px solid #e8e8e4; background:#fff; color:#888;
      border-radius:10px; padding:11px; cursor:pointer;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:500;
      transition:background .15s, border-color .15s, color .15s;
    }
    .rm-btn-cancel:hover { background:#f5f5f2; border-color:#ddd; color:#555; }

    .rm-btn-submit {
      flex:2; border:none; background:#6cbb23; color:#fff;
      border-radius:10px; padding:11px; cursor:pointer;
      font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700;
      position:relative; overflow:hidden;
      transition:transform .18s, box-shadow .18s;
    }
    .rm-btn-submit::after {
      content:''; position:absolute; inset:0;
      background:rgba(255,255,255,.12); opacity:0; transition:opacity .18s;
    }
    .rm-btn-submit:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 10px 28px rgba(108,187,35,.38); }
    .rm-btn-submit:hover:not(:disabled)::after { opacity:1; }
    .rm-btn-submit:active:not(:disabled) { transform:scale(.97); }
    .rm-btn-submit:disabled { background:#c8e89c; cursor:not-allowed; }

    .rm-spinner {
      width:14px; height:14px; border-radius:50%; flex-shrink:0;
      border:2px solid rgba(255,255,255,.3); border-top-color:#fff;
      animation:rm-spin .7s linear infinite; display:inline-block;
    }

    /* ── Close button ── */
    .rm-close {
      width:28px; height:28px; border-radius:8px;
      border:1.5px solid rgba(255,255,255,.2);
      background:rgba(255,255,255,.08);
      display:flex; align-items:center; justify-content:center;
      cursor:pointer; transition:background .15s;
    }
    .rm-close:hover { background:rgba(255,255,255,.18); }

    /* ── Step badge ── */
    .rm-step {
      width:22px; height:22px; border-radius:50%;
      background:rgba(108,187,35,.15); border:1.5px solid rgba(108,187,35,.3);
      display:flex; align-items:center; justify-content:center; flex-shrink:0;
      font-size:10px; font-weight:700; color:#6cbb23;
      font-family:'Plus Jakarta Sans',sans-serif;
    }

    /* ── Lbl ── */
    .rm-lbl {
      display:block; font-size:10px; font-weight:700; color:#b0b0aa;
      letter-spacing:.07em; text-transform:uppercase; margin-bottom:4px;
    }

    /* ── Cross accent ── */
    .rm-cx::before,.rm-cx::after { content:''; position:absolute; background:rgba(255,255,255,.12); }
    .rm-cx::before { left:50%; top:0; bottom:0; width:1.5px; transform:translateX(-50%); }
    .rm-cx::after  { top:50%; left:0; right:0; height:1.5px; transform:translateY(-50%); }
  `}</style>
);

/* ─────────────────────────────────────────────
   MAIN COMPONENT — LOGIC UNCHANGED
───────────────────────────────────────────── */
export default function ReferralModal({ isOpen, onClose, userEmail }) {
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.name || !form.phone) {
      alert("Please fill Name and Phone Number");
      return;
    }
    try {
      setLoading(true);
      await addDoc(collection(db, "referrals"), {
        name: form.name,
        phone: form.phone,
        address: form.address || "",
        referredBy: userEmail,
        status: "pending",
        rewardGiven: false,
        createdAt: serverTimestamp(),
      });
      const message = `Hi Zafflo team,\n\nI want to refer a business 👇\n\nBusiness Name: ${form.name}\nPhone: ${form.phone}\nAddress: ${form.address || "N/A"}\n\nReferred By: ${userEmail}\n\nPlease contact them.`;
      window.open(`https://wa.me/918097588099?text=${encodeURIComponent(message)}`, "_blank");
      onClose();
      setForm({ name: "", phone: "", address: "" });
    } catch (error) {
      console.error(error);
      alert("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const howItWorks = [
    { n:"1", text:"Fill in your friend's business details" },
    { n:"2", text:"We reach out and help them sign up"     },
    { n:"3", text:"Both of you get 1 month FREE"          },
  ];

  return (
    <>
      <Styles />

      {/* Backdrop */}
      <div
        className="rm-backdrop rm-root"
        onClick={onClose}
        style={{
          position:"fixed", inset:0, zIndex:50,
          background:"rgba(8,12,8,.72)",
          backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)",
          display:"flex", alignItems:"center", justifyContent:"center",
          padding:"16px",
        }}
      >
        {/* Modal card */}
        <div
          className="rm-card"
          onClick={e => e.stopPropagation()}
          style={{
            width:"100%", maxWidth:440,
            background:"#fff",
            borderRadius:22,
            overflow:"hidden",
            boxShadow:"0 32px 80px rgba(0,0,0,.35), 0 4px 16px rgba(0,0,0,.12)",
          }}
        >

          {/* ── DARK HEADER ── */}
          <div style={{
            background:"linear-gradient(160deg,#0c1a0c 0%,#152815 60%,#0a120a 100%)",
            padding:"22px 24px 20px",
            position:"relative", overflow:"hidden",
          }}>
            {/* grid */}
            <div style={{ position:"absolute",inset:0,pointerEvents:"none",
              backgroundImage:"linear-gradient(rgba(108,187,35,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(108,187,35,.05) 1px,transparent 1px)",
              backgroundSize:"32px 32px"}}/>
            {/* glow */}
            <div style={{ position:"absolute",top:"-40%",left:"50%",transform:"translateX(-50%)",
              width:320,height:200,borderRadius:"50%",
              background:"radial-gradient(ellipse,rgba(108,187,35,.16) 0%,transparent 70%)",
              pointerEvents:"none"}}/>
            {/* corner crosses */}
            {[[1,1],[1,0]].map(([t,l],i)=>(
              <div key={i} className="rm-cx" style={{
                position:"absolute", width:12, height:12,
                ...(t?{top:16}:{bottom:16}), ...(l?{left:18}:{right:18}),
              }}/>
            ))}

            <div style={{ position:"relative", zIndex:1 }}>
              {/* Top row: emoji badge + close */}
              <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:12 }}>
                <div style={{
                  display:"inline-flex", alignItems:"center", gap:6,
                  background:"rgba(108,187,35,.15)", border:"1px solid rgba(108,187,35,.28)",
                  borderRadius:999, padding:"4px 11px",
                  fontSize:11, fontWeight:700, color:"#6cbb23",
                  fontFamily:"'Plus Jakarta Sans',sans-serif",
                }}>
                  <span style={{ width:5, height:5, borderRadius:"50%", background:"#6cbb23",
                    display:"inline-block", animation:"rm-pulse 1.6s ease-in-out infinite" }}/>
                  Refer & Earn
                </div>
                <button className="rm-close" onClick={onClose} aria-label="Close">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
                    <path d="M1 1l9 9M10 1L1 10" stroke="rgba(255,255,255,.7)" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>

              {/* Headline */}
              <h2 className="rm-zh" style={{
                fontSize:"clamp(20px,3vw,26px)", fontWeight:900, fontStyle:"italic",
                color:"#fff", lineHeight:1.1, letterSpacing:"-0.02em", marginBottom:6,
              }}>
                Invite a business,
                <br/>
                <span className="rm-shine">both get a free month.</span>
              </h2>
              <p style={{ fontSize:12, color:"rgba(255,255,255,.4)", fontWeight:300, lineHeight:1.55 }}>
                After their paid signup — you both get <strong style={{ color:"rgba(255,255,255,.7)", fontWeight:600 }}>1 month free</strong> added to your account.
              </p>
            </div>
          </div>

          {/* ── HOW IT WORKS STRIP ── */}
          <div style={{
            display:"flex", alignItems:"stretch", gap:0,
            background:"#f5f5f2", borderBottom:"1px solid #edede9",
          }}>
            {howItWorks.map(({ n, text }, i) => (
              <div key={n} style={{
                flex:1, padding:"10px 12px",
                borderRight: i < 2 ? "1px solid #e8e8e4" : "none",
                display:"flex", flexDirection:"column", alignItems:"center", gap:5,
              }}>
                <div className="rm-step">{n}</div>
                <div style={{ fontSize:10, color:"#888", textAlign:"center", lineHeight:1.45,
                  fontFamily:"'Plus Jakarta Sans',sans-serif" }}>{text}</div>
              </div>
            ))}
          </div>

          {/* ── FORM ── */}
          <div style={{ padding:"20px 24px 22px" }}>

            {/* Business name */}
            <div style={{ marginBottom:10 }}>
              <label className="rm-lbl">Their Business Name</label>
              <input
                type="text"
                placeholder="e.g. Hotel Bliss, Glamour Salon"
                className="rm-field"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom:10 }}>
              <label className="rm-lbl">Phone Number</label>
              <input
                type="tel"
                placeholder="e.g. 9876543210"
                className="rm-field"
                value={form.phone}
                onChange={e => setForm({ ...form, phone: e.target.value })}
              />
            </div>

            {/* Address */}
            <div style={{ marginBottom:18 }}>
              <label className="rm-lbl">Address <span style={{ color:"#ccc", fontWeight:400, textTransform:"none", letterSpacing:0 }}>(optional)</span></label>
              <textarea
                rows={2}
                placeholder="City, area — helps us reach them faster"
                className="rm-field"
                value={form.address}
                onChange={e => setForm({ ...form, address: e.target.value })}
              />
            </div>

            {/* Buttons */}
            <div style={{ display:"flex", gap:10, marginBottom:14 }}>
              <button className="rm-btn-cancel" onClick={onClose}>Cancel</button>
              <button
                className="rm-btn-submit"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}>
                    <span className="rm-spinner"/>
                    Submitting…
                  </span>
                ) : (
                  <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}>
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                      <path d="M7 1.2C3.8 1.2 1.2 3.8 1.2 7c0 .98.26 1.9.7 2.7L1.2 12.8l3.17-1.48A5.77 5.77 0 007 12.8c3.2 0 5.8-2.6 5.8-5.8S10.2 1.2 7 1.2z" fill="#fff"/>
                    </svg>
                    Submit & Share via WhatsApp
                  </span>
                )}
              </button>
            </div>

            {/* Footer note */}
            <div style={{
              display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"9px 12px",
              background:"#f8fdf4", border:"1.5px solid #d8f0b8",
              borderRadius:10,
            }}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6" cy="6" r="5.25" stroke="#6cbb23" strokeWidth="1.2"/>
                <path d="M6 4v2.5l1.5 1" stroke="#6cbb23" strokeWidth="1.2" strokeLinecap="round"/>
              </svg>
              <span style={{ fontSize:11, color:"#5a8c1a", fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
                We'll contact them and reward both after successful signup.
              </span>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}